import { lazy, Suspense, useEffect, useState } from "react";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthGate } from "./auth/AuthGate";
import { LoginPage } from "./auth/LoginPage";
import { OidcCallbackPage } from "./auth/OidcCallbackPage";
import { AppShell } from "./shell/AppShell";
import { useAuthStore } from "./auth/authStore";

const DashboardModule = lazy(() =>
  import("./modules/dashboard/DashboardModule").then((m) => ({ default: m.DashboardModule })),
);
const TradesModule = lazy(() =>
  import("./modules/trades/TradesModule").then((m) => ({ default: m.TradesModule })),
);
const CurvesModule = lazy(() =>
  import("./modules/curves/CurvesModule").then((m) => ({ default: m.CurvesModule })),
);
const ReportsModule = lazy(() =>
  import("./modules/reports/ReportsModule").then((m) => ({ default: m.ReportsModule })),
);
const RiskModule = lazy(() =>
  import("./modules/risk/RiskModule").then((m) => ({ default: m.RiskModule })),
);
const ReconciliationModule = lazy(() =>
  import("./modules/reconciliation/ReconciliationModule").then((m) => ({
    default: m.ReconciliationModule,
  })),
);
const AdminModule = lazy(() =>
  import("./modules/admin/AdminModule").then((m) => ({ default: m.AdminModule })),
);
const AccountModule = lazy(() =>
  import("./modules/account/AccountModule").then((m) => ({ default: m.AccountModule })),
);
const TradePadModule = lazy(() =>
  import("./modules/tradepad/TradePadModule").then((m) => ({ default: m.TradePadModule })),
);
const CashModule = lazy(() =>
  import("./modules/cash/CashModule").then((m) => ({ default: m.CashModule })),
);
const InstrumentsModule = lazy(() =>
  import("./modules/instruments/InstrumentsModule").then((m) => ({
    default: m.InstrumentsModule,
  })),
);
const DataHubModule = lazy(() =>
  import("./modules/data-hub/DataHubModule").then((m) => ({ default: m.DataHubModule })),
);
const MiddleOfficeModule = lazy(() =>
  import("./modules/middle-office/MiddleOfficeModule").then((m) => ({
    default: m.MiddleOfficeModule,
  })),
);

function ReconciliationRedirect() {
  const loc = useLocation();
  return <Navigate to={`/recon${loc.search}`} replace />;
}

function AnalysisRedirect() {
  const loc = useLocation();
  return <Navigate to={`/instruments${loc.search}`} replace />;
}

function SettlementRedirect() {
  const loc = useLocation();
  if (loc.pathname.includes("statement-recon")) {
    return <Navigate to="/data-hub?preset=statement-recon" replace />;
  }
  return <Navigate to="/data-hub?preset=settlement-ops" replace />;
}

function RefdataRedirect() {
  const loc = useLocation();
  const params = new URLSearchParams(loc.search);
  const instrument = params.get("instrument");
  if (instrument) {
    const rest = new URLSearchParams(loc.search);
    rest.delete("instrument");
    const qs = rest.toString();
    return (
      <Navigate
        to={`/instruments/${encodeURIComponent(instrument)}${qs ? `?${qs}` : ""}`}
        replace
      />
    );
  }
  return <Navigate to={`/instruments${loc.search}`} replace />;
}

function ModuleFallback() {
  return (
    <div className="flex h-full items-center justify-center font-mono text-sm text-muted">
      Loading module…
    </div>
  );
}

function LoginRoute() {
  const loc = useLocation();
  const accessToken = useAuthStore((s) => s.accessToken);
  const [hydrated, setHydrated] = useState(() => useAuthStore.persist.hasHydrated());

  useEffect(() => {
    const unsub = useAuthStore.persist.onFinishHydration(() => setHydrated(true));
    void useAuthStore.persist.rehydrate();
    return unsub;
  }, []);

  if (!hydrated) {
    return (
      <div className="flex h-screen items-center justify-center bg-bg text-muted font-mono text-sm">
        Loading session…
      </div>
    );
  }

  if (accessToken) {
    const from = loc.state as { from?: { pathname?: string } } | undefined;
    const to = from?.from?.pathname ?? "/";
    return <Navigate to={to || "/"} replace />;
  }

  // No access token in memory — AuthGate will attempt cookie-based restore when user navigates to /.
  // If the httpOnly refresh cookie is still valid, AuthGate redirects back without showing the login page.

  return <LoginPage />;
}

export default function App() {
  return (
    <Routes>
      <Route path="/login" element={<LoginRoute />} />
      <Route path="/auth/login" element={<LoginRoute />} />
      <Route path="/auth/oidc/callback" element={<OidcCallbackPage />} />

      <Route element={<AuthGate />}>
        <Route element={<AppShell />}>
          <Route
            index
            element={
              <Suspense fallback={<ModuleFallback />}>
                <DashboardModule />
              </Suspense>
            }
          />
          <Route
            path="trades/*"
            element={
              <Suspense fallback={<ModuleFallback />}>
                <TradesModule />
              </Suspense>
            }
          />
          <Route
            path="curves/*"
            element={
              <Suspense fallback={<ModuleFallback />}>
                <CurvesModule />
              </Suspense>
            }
          />
          <Route path="refdata/*" element={<RefdataRedirect />} />
          <Route
            path="instruments/*"
            element={
              <Suspense fallback={<ModuleFallback />}>
                <InstrumentsModule />
              </Suspense>
            }
          />
          <Route path="settlement/*" element={<SettlementRedirect />} />
          <Route
            path="cash/*"
            element={
              <Suspense fallback={<ModuleFallback />}>
                <CashModule />
              </Suspense>
            }
          />
          <Route
            path="reports/*"
            element={
              <Suspense fallback={<ModuleFallback />}>
                <ReportsModule />
              </Suspense>
            }
          />
          <Route
            path="risk/*"
            element={
              <Suspense fallback={<ModuleFallback />}>
                <RiskModule />
              </Suspense>
            }
          />
          <Route path="reconciliation/*" element={<ReconciliationRedirect />} />
          <Route
            path="recon/*"
            element={
              <Suspense fallback={<ModuleFallback />}>
                <ReconciliationModule />
              </Suspense>
            }
          />
          <Route
            path="middle-office/*"
            element={
              <Suspense fallback={<ModuleFallback />}>
                <MiddleOfficeModule />
              </Suspense>
            }
          />
          <Route path="fixing-monitor" element={<Navigate to="/data-hub" replace />} />
          <Route
            path="data-hub/*"
            element={
              <Suspense fallback={<ModuleFallback />}>
                <DataHubModule />
              </Suspense>
            }
          />
          <Route
            path="admin/*"
            element={
              <Suspense fallback={<ModuleFallback />}>
                <AdminModule />
              </Suspense>
            }
          />
          <Route
            path="account/*"
            element={
              <Suspense fallback={<ModuleFallback />}>
                <AccountModule />
              </Suspense>
            }
          />
          <Route path="etrade/*" element={<Navigate to="/tradepad" replace />} />
          <Route
            path="tradepad/*"
            element={
              <Suspense fallback={<ModuleFallback />}>
                <TradePadModule />
              </Suspense>
            }
          />
          <Route path="analysis/*" element={<AnalysisRedirect />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Route>
      </Route>
    </Routes>
  );
}
